import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import apiClient from '../utils/api';

interface CacheContextType {
    invalidateCache: () => void;
    lastInvalidated: number;
    refreshDashboard: () => Promise<void>;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export function CacheProvider({ children }: { children: ReactNode }) {
    const [lastInvalidated, setLastInvalidated] = useState<number>(Date.now());

    const invalidateCache = useCallback(() => {
        const timestamp = Date.now();
        apiClient.invalidateCache();
        setLastInvalidated(timestamp);
        // Dispatch event to notify all components to refresh
        window.dispatchEvent(new CustomEvent('cache-invalidated', { detail: { timestamp } }));
    }, []);

    const refreshDashboard = useCallback(async () => {
        invalidateCache();
        // Force a small delay to ensure cache is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
    }, [invalidateCache]);

    return (
        <CacheContext.Provider value={{ invalidateCache, lastInvalidated, refreshDashboard }}>
            {children}
        </CacheContext.Provider>
    );
}

export function useCache() {
    const context = useContext(CacheContext);
    if (!context) {
        throw new Error('useCache must be used within CacheProvider');
    }
    return context;
}

